import { LtrNumberRatio } from "@shared/frontend/components/LtrNumberRatio";
import InsightsIcon from "@mui/icons-material/Insights";
import {
  Alert,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { type FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getErrorMessage } from "../../auth/Auth";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import {
  fetchRegistrationTracking,
  REGISTRATION_TRACKING_PAGE_SIZE,
} from "./registrationTrackingApi";
import {
  clampDateToOnFromChange,
  registrationTrackingDefaultDay,
  validateRegistrationDateRange,
} from "./registrationTrackingDateUtils";
import {
  allowsGroupVolunteer,
  OPPORTUNITY_VOLUNTEER_MODE_HEB,
  resolveOpportunityVolunteerMode,
} from "@shared/helpers/opportunity-volunteer-mode";
import { usePersistedRegistrationDateRange } from "./usePersistedRegistrationDateRange";
import "./OpportunitiesRegistrationTracking.scss";

export type RegistrantTypeFilter = "both" | "individual" | "group";

export interface HyperFiltersForRegistration {
  categoryIds: string[];
  subCategoryId: string | null;
  teamIds: string[];
  populationIds: string[];
  partnerOrganizationIds?: string[];
}

interface OpportunitiesRegistrationTrackingProps {
  hyperFilters: HyperFiltersForRegistration;
}

export const OpportunitiesRegistrationTracking: FC<
  OpportunitiesRegistrationTrackingProps
> = ({ hyperFilters }) => {
  const { currOrganization } = useOrganization();
  const navigate = useNavigate();
  const { dateFrom, setDateFrom, dateTo, setDateTo } =
    usePersistedRegistrationDateRange(currOrganization?.id);
  const [registrantType, setRegistrantType] =
    useState<RegistrantTypeFilter>("both");

  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        categoryIds: hyperFilters.categoryIds,
        subCategoryId: hyperFilters.subCategoryId,
        teamIds: hyperFilters.teamIds,
        populationIds: hyperFilters.populationIds,
        partnerOrganizationIds: hyperFilters.partnerOrganizationIds,
        registrantType,
      }),
    [hyperFilters, registrantType],
  );

  const scopeKey = [
    currOrganization?.id ?? "",
    dateFrom?.format("YYYY-MM-DD") ?? "",
    dateTo?.format("YYYY-MM-DD") ?? "",
    filtersKey,
  ].join("|");

  const [page, setPage] = useState(0);
  const [pageScopeKey, setPageScopeKey] = useState(scopeKey);

  // Reset page synchronously when filter/date scope changes (avoid stale page fetch).
  if (pageScopeKey !== scopeKey) {
    setPageScopeKey(scopeKey);
    setPage(0);
  }

  const dateRangeError = useMemo(
    () => validateRegistrationDateRange(dateFrom, dateTo),
    [dateFrom, dateTo],
  );

  const queryBody = useMemo(() => {
    if (!currOrganization?.id || !dateFrom || !dateTo || dateRangeError) {
      return null;
    }
    return {
      organizationId: currOrganization.id,
      dateFrom: dateFrom.format("YYYY-MM-DD"),
      dateTo: dateTo.format("YYYY-MM-DD"),
      page: page + 1,
      pageSize: REGISTRATION_TRACKING_PAGE_SIZE,
      registrantType,
      categoryIds: hyperFilters.categoryIds.length
        ? hyperFilters.categoryIds
        : undefined,
      subCategoryId: hyperFilters.subCategoryId ?? undefined,
      teamIds: hyperFilters.teamIds.length ? hyperFilters.teamIds : undefined,
      populationIds: hyperFilters.populationIds.length
        ? hyperFilters.populationIds
        : undefined,
      partnerOrganizationIds: hyperFilters.partnerOrganizationIds?.length
        ? hyperFilters.partnerOrganizationIds
        : undefined,
    };
  }, [
    currOrganization?.id,
    dateFrom,
    dateTo,
    dateRangeError,
    page,
    filtersKey,
    registrantType,
    hyperFilters.categoryIds,
    hyperFilters.subCategoryId,
    hyperFilters.teamIds,
    hyperFilters.populationIds,
    hyperFilters.partnerOrganizationIds,
  ]);

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ["registration-tracking", queryBody],
    queryFn: () => fetchRegistrationTracking(queryBody!),
    enabled: !!queryBody,
    staleTime: 1000 * 60,
    placeholderData: (previous) => previous,
  });

  const totalCount = data?.totalCount ?? data?.opportunities.length ?? 0;
  const registrationsInRangeTotal =
    data?.registrationsInRangeTotal ??
    (data?.opportunities ?? []).reduce(
      (sum, row) => sum + row.registrationsInRange,
      0,
    );

  if (!currOrganization) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack className="opportunities-registration-tracking" gap={2} flex={1}>
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <InsightsIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            מעקב הרשמות
          </Typography>
        </Stack>
        <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
          <ToggleButtonGroup
            exclusive
            size="small"
            value={registrantType}
            onChange={(_, value: RegistrantTypeFilter | null) => {
              if (value) setRegistrantType(value);
            }}
          >
            <ToggleButton value="both">גם וגם</ToggleButton>
            <ToggleButton value="individual">יחיד</ToggleButton>
            <ToggleButton value="group">קבוצה</ToggleButton>
          </ToggleButtonGroup>
          <DatePicker
            label="מתאריך"
            value={dateFrom}
            maxDate={dateTo ?? undefined}
            onChange={(value) => {
              setDateFrom(value);
              setDateTo((prev) => clampDateToOnFromChange(value, prev));
            }}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="עד תאריך"
            value={dateTo}
            minDate={dateFrom ?? undefined}
            maxDate={registrationTrackingDefaultDay()}
            onChange={setDateTo}
            slotProps={{ textField: { size: "small" } }}
          />
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary">
        מאושרים בטווח: לפי תאריך מאצ' (matched_at או created_at, שעון ישראל),
        סכום מאצ'ים לפי מכפיל המאצ'ים, וכשיש קבוצה גם לפי גודל הקבוצה. מעוניינים בטווח: לפי תאריך יצירה, ספירת
        שורות ממתינות. תפוסה = אותה נוסחה כמו בטבלת המשימות (בודדים מאושרים +
        גודל קבוצות). ממוין לפי הרשמות בטווח; טעינה לפי עמודים כדי לא לעצור את
        המערכת.
      </Typography>

      {dateRangeError && (
        <Alert severity="warning">{dateRangeError}</Alert>
      )}

      {isError && !dateRangeError && (
        <Alert severity="error">{getErrorMessage(error)}</Alert>
      )}

      {!dateRangeError && isLoading && !data && (
        <Stack alignItems="center" py={6}>
          <CircularProgress />
        </Stack>
      )}

      {!dateRangeError && !isError && data && (
        <Stack className="opportunities-registration-tracking__body" gap={2}>
          <Paper variant="outlined" sx={{ p: 1.5, flexShrink: 0 }}>
            <Typography variant="body2">
              <strong>הרשמות מאושרות בטווח (סה״כ):</strong>{" "}
              {registrationsInRangeTotal}
              {" · "}
              <strong>משימות:</strong> {data.opportunities.length}
              {totalCount > data.opportunities.length
                ? ` מתוך ${totalCount}`
                : null}
              {isFetching && !isLoading ? " · טוען…" : null}
            </Typography>
          </Paper>

          <TableContainer
            component={Paper}
            variant="outlined"
            className="opportunities-registration-tracking__table-container"
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>משימה</TableCell>
                  <TableCell>ארגון שותף</TableCell>
                  <TableCell align="center">משימה ממושכת</TableCell>
                  <TableCell align="center">תפוסה</TableCell>
                  <TableCell align="center">הרשמות בטווח</TableCell>
                  <TableCell align="center">מעוניינים בטווח</TableCell>
                  <TableCell align="center">ממתינים (סה״כ)</TableCell>
                  <TableCell align="center">תאריך משימה</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.opportunities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      אין משימות בטווח / בסינון שנבחר
                    </TableCell>
                  </TableRow>
                ) : (
                  data.opportunities.map((row) => (
                    <TableRow
                      key={row.opportunityId}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(`/events/${row.opportunityId}`, {
                          state: { selectedList: "registration-analytics" },
                        })
                      }
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {row.title}
                        </Typography>
                        {allowsGroupVolunteer(row.volunteerMode) && (
                          <Typography variant="caption" color="text.secondary">
                            {
                              OPPORTUNITY_VOLUNTEER_MODE_HEB[
                                resolveOpportunityVolunteerMode(
                                  row.volunteerMode,
                                )
                              ]
                            }
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.partnerOrganizationName?.trim() || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {row.isLongTerm ? "כן" : "לא"}
                      </TableCell>
                      <TableCell align="center">
                        <LtrNumberRatio
                          left={row.approvedTotal}
                          right={row.amount}
                        />
                        {row.fillRatePercent != null && (
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            {row.fillRatePercent}%
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <strong>{row.registrationsInRange}</strong>
                      </TableCell>
                      <TableCell align="center">
                        {row.interestedInRange}
                      </TableCell>
                      <TableCell align="center">{row.pendingTotal}</TableCell>
                      <TableCell align="center">
                        {row.startDate
                          ? dayjs(row.startDate).format("DD/MM/YYYY")
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, next) => setPage(next)}
              rowsPerPage={REGISTRATION_TRACKING_PAGE_SIZE}
              rowsPerPageOptions={[REGISTRATION_TRACKING_PAGE_SIZE]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} מתוך ${count !== -1 ? count : `יותר מ-${to}`}`
              }
              labelRowsPerPage="לעמוד"
            />
          </TableContainer>
        </Stack>
      )}
    </Stack>
  );
};
